<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="instrument-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row>
          <b-col class="pr-0">
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="instrument_tests.analysis.analyte.sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event)"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="instrument_tests.analysis.analyte_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('analyte', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="name" slot-scope="{ row }">
        <a :href="`#/instrument/${row.id}`" class="btn-link">{{ row.name }}</a>
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit instrument')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/instrument/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete instrument')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import AnalyteModel from "@/models/lab/AnalyteModel";
import BaseServerTable from "@/components/shared/BaseServerTable";
import InstrumentCollection from "@/collections/lab/InstrumentCollection";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck"
import { getCollection } from "@/shared/fetch-utils";

Vue.use(ServerTable);

export default {
  name: "InstrumentServerTable",
  extends: BaseServerTable,
  components: {
    ModalTrashBasket,
    ServerTable,
    SelectFilter
  },
  props: {
    collection: {
      type: Object,
      default: () => new InstrumentCollection()
    },
    sample_types: {
      type: Object,
      default: () => null
    },
    analytes: {
      type: Object,
      default: () => null
    },
    task_instruments: {
      type: Array,
      default: () => null
    },
    show_columns: {
      type: Array,
      default: () => ["name", "abbrev", "actions"]
    },
    filterable: {
      type: Array,
      default: () => ["searchable"]
    },
    sortable: {
      type: Array,
      default: () => ["name", "abbrev"]
    }
  },
  data() {
    const actions_access = this.checkPermission("edit instrument")
      || this.checkPermission("delete instrument");

    return {
      entity_type: "instrument",
      analysis_id: this.$route.params.analysis_id || null,
      analyte_collection: null,
      sample_type_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            name: "Name",
            abbrev: "Abbrev.",
            actions: "Actions"
          }
        }
      })
    };
  },
  computed: {
    ...mapGetters({
      trash: "instrumentTrash"
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection
      }
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection
      }
    },
    show_modal() {
      return this.checkPermission('delete instrument') && this.show_columns.includes("actions")
    }
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    prepareCollectionFilter(filter) {
      this.analysis_id &&
        filter.filterEquals("instrument_tests.analysis_id", this.analysis_id);
      this.task_instruments &&
        filter.filterIn("instrument_instances.instrument_id", this.task_instruments);
        
      return filter;
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find(item => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    async init() {
      const payload = {
        message: this.$bvToast,
        sort: [
          {
            logic: "sortAddAsc",
            field: "name"
          }
        ],
        limit: "all",
      };

      !this.analytes &&
        (this.analyte_collection = await getCollection({
          ...payload,
          collection_type: "analyte"
        }));
      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type"
        }));
    },
  },
  created() {
    this.init();
  }
};
</script>
